import React, { useMemo } from 'react';

import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { sort } from 'ramda';
import { Helmet } from 'react-helmet';
import { AlineCheckbox, Spacer, useListToolbarStyles } from 'src/components';
import { NameCell, NewUsersMenu, RoleCell } from 'src/components/admin';
import { Apollo } from 'src/gql/generated';
import { compareByEmail, getUserName, overflowMenuCSSId } from 'src/helpers';
import { createDataGridPremiumFooterlessPaperStyles, useGetOrgMembers } from 'src/hooks';
import type { UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  deactivatedRow: {
    opacity: 0.5,
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover #${overflowMenuCSSId}`]: {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

type NameCellUser = UserSubset & Pick<Apollo.User, 'accountStatus'>;
interface RoleCellUser extends Pick<
  Apollo.User,
  | 'displayName'
  | 'email'
  | 'firstName'
  | 'id'
  | 'jobTitle'
  | 'lastName'
  | 'role'
  | '__typename'
  | 'isDeactivated'
> {
  org: Pick<Apollo.Org, 'id' | '__typename'> | null;
}
export interface UsersListRow extends Pick<Apollo.User, 'email' | 'id' | 'isDeactivated'> {
  name: NameCellUser;
  role: RoleCellUser;
}
type UserGridDef<CellValue, FormattedValue = string> = GridColDef<
  UsersListRow,
  CellValue,
  FormattedValue
>;

export function UsersList(): JSX.Element {
  const { classes } = useStyles();
  const { loading, orgMembers } = useGetOrgMembers({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      filter: Apollo.OrgMemberFilter.All,
    },
  });
  const columnDefs: [
    UserGridDef<NameCellUser, string>,
    UserGridDef<string, string>,
    UserGridDef<RoleCellUser, string>,
  ] = [
    {
      field: 'name',
      flex: 1,
      headerName: 'Name',
      renderCell: (params: GridRenderCellParams<UsersListRow, NameCellUser, string>) => (
        <NameCell {...params} />
      ),
      sortComparator: (v1: NameCellUser, v2: NameCellUser) =>
        getUserName(v1).localeCompare(getUserName(v2)),
    },
    {
      field: 'email',
      flex: 1,
      headerName: 'Email',
    },
    {
      field: 'role',
      flex: 1,
      headerName: 'Role',
      renderCell: (params: GridRenderCellParams<UsersListRow, RoleCellUser, string>) => (
        <RoleCell {...params} />
      ),
      sortComparator: (v1, v2) => v1.role.localeCompare(v2.role),
    },
  ];
  const rows: UsersListRow[] = useMemo(() => {
    const sortedOrgMembers = sort(compareByEmail, orgMembers);

    return sortedOrgMembers.map<UsersListRow>(user => ({
      email: user.email,
      id: user.id,
      isDeactivated: user.isDeactivated,
      name: user,
      role: user,
    }));
  }, [orgMembers]);

  return (
    <div className={classes.listContainer}>
      <Helmet>
        <title>User Admin</title>
      </Helmet>
      <DataGridPremium
        disableColumnFilter
        disableColumnMenu
        disableColumnReorder
        disableColumnResize
        disableRowSelectionOnClick
        hideFooter
        showToolbar
        className={classes.xGrid}
        columns={columnDefs}
        getRowClassName={({ row }) => {
          return row.isDeactivated ? classes.deactivatedRow : '';
        }}
        loading={loading}
        rows={rows}
        slotProps={{
          loadingOverlay: {
            noRowsVariant: 'linear-progress',
            variant: 'linear-progress',
          },
        }}
        slots={{
          baseCheckbox: AlineCheckbox,
          toolbar: UsersListToolbar,
        }}
        sx={createDataGridPremiumFooterlessPaperStyles}
      />
    </div>
  );
}

function UsersListToolbar(): JSX.Element {
  const { classes } = useListToolbarStyles();

  return (
    <div className={classes.toolbar}>
      <Spacer />
      <NewUsersMenu />
    </div>
  );
}
